import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    output,
    viewChild,
    ViewEncapsulation,
    input,
    effect,
} from "@angular/core";
import { Debounce } from "../../decorators/debounce.decorator";
import { AngBridgeUtil } from "../../util/ang.bridge.util";
import { S25ItemComponent } from "../s25-item/s25.item.component";
import { Table } from "../s25-table/Table";
import { Bind } from "../../decorators/bind.decorator";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { Item } from "../../pojo/Item";
import { S25ItemI } from "../../pojo/S25ItemI";
import { S25Const } from "../../util/s25-const";
import { S25ObjectConflictComponent } from "./s25.object.conflict.component";
import { S25ObjectAddButtonComponent } from "./s25.object.add.button.component";
import { FlsStore } from "../../stores/fls.store";
import { ObjectSearchRefresh } from "./s25.object.search.component";
import { ObjectSearchAvail } from "./object.search.avail";
import {
    S25ObjectSearchUtil,
    AvailLocationItems,
    AvailResourceItems,
    UrlParams,
    Layout,
    LayoutTimesDuration,
} from "./s25.object.search.util";
import { Eval25Service } from "../../services/eval25.service";
import { S25ObjectMap } from "./s25.object.search.config";
import { SearchService } from "../../services/search/search.service";
import { ListService } from "../../services/list.service";
import { S25Event } from "../s25-event/EventMicroI";
import { S25Reservation, S25RmReservation, S25RsReservation } from "../s25-event/ReservationI";
import { S25EditableTextareaComponent } from "../s25-editable/s25-editable-textarea/s25.editable.textarea.component";
import { S25ReservationUtil } from "../s25-event/utils/s25.reservation.util";
import { SpaceService } from "../../services/space.service";
import { ResourceService } from "../../services/resource.service";
import { AvailWSResponse } from "../../services/resource.space.avail.service";
import WSSpaceAvail = AvailWSResponse.WSSpaceAvail;
import WSResourceAvail = AvailWSResponse.WSResourceAvail;
import DateElement = AvailWSResponse.DateElement;
import { CheckedSpaceReservation, CheckedResourceReservation } from "./s25.object.search.util";
type AddItemType = AvailLocationItems | AvailResourceItems;
import { S25ModalComponent } from "../s25-modal/s25.modal.component";
import { DropDownItem } from "../../pojo/DropDownItem";

interface SearchResponseData {
    [key: string]: {
        total_results?: number;
        paginate_key?: number | string;
        page_num?: number;
        page_count?: number;
        [key: string]: any;
    };
}

const objectSearchSortColumnMap: Record<string, string> = {
    rating_avg: "locationSatisfaction",
};

@Component({
    selector: "s25-ng-object-search-results-list",
    template: `
        @if (fls.loaded() && !displayLocationScore.isLoading()) {
            @if (allowSelection()) {
                <div class="buttons">
                    <button
                        class="aw-button aw-button--primary c-margin-right--single"
                        (click)="saveMulitSelectedItems()"
                        [disabled]="!isMultiSelected"
                    >
                        {{ newAddedItemsList.length > 1 ? addSelectedButton() + "s" : addSelectedButton() }}
                    </button>
                </div>
            }
            <div>
                <s25-ng-table
                    [columnSortable]="true"
                    [dataSource]="tableData"
                    [stickyHeader]="true"
                    [stickyColumnSortPreference]="S25Const.itemId2Name[itemTypeId()]"
                    [unlimitedWidth]="true"
                    [pivotThreshold]="650"
                    [hasColumnChooser]="!isEventForm()"
                />
                @if (!isEventForm() || tableComponent()?.hasScrollBar()) {
                    <div class="buttons c-margin-top--single c-margin-bottom--single">
                        @if (!isEventForm()) {
                            <button class="aw-button aw-button--primary c-margin-right--single" (click)="onCancel()">
                                Close
                            </button>
                        }
                        @if (tableComponent()?.hasScrollBar()) {
                            <button
                                class="aw-button aw-button--outline c-margin-right--single"
                                (click)="scrollTableToTop()"
                            >
                                Return to Top
                            </button>
                        }
                    </div>
                }
            </div>

            <s25-ng-modal #layoutConflictModal [title]="'Layout Setup/Takedown times'" [size]="'md'">
                <ng-template #s25ModalBody>
                    <layout-times
                        [setup]="layoutDetail.setup"
                        [takedown]="layoutDetail.takedown"
                        [itemName]="layoutDetail.itemName"
                        [conflicts]="layoutDetail.conflicts"
                        [canEditSetup]="canEditLayoutSetup()"
                        [canEditTakedown]="canEditLayoutTakedown()"
                        (valuesChanged)="layoutTimesChange($event)"
                    />
                </ng-template>
            </s25-ng-modal>
        }
    `,
    styles: `
        :host ::ng-deep table {
            width: 100% !important;
        }
        :host ::ng-deep tbody tr td input[type="checkbox"] {
            height: 16px !important;
        }

        :host ::ng-deep s25-item {
            /* only applies to (private) items */
            color: var(--color--text-muted);
        }

        :host ::ng-deep s25-item s25-popover {
            display: block !important;
        }
        :host ::ng-deep .s25-ng-table--table {
            tr {
                &:first-child {
                    td,
                    th {
                        border-top: none;
                    }
                }
                &:last-child {
                    td {
                        border-bottom: none;
                    }
                }
                th {
                    --color--table-border: var(--color--border);
                }
                td,
                th {
                    border: 1px solid var(--color--table-border);

                    &:first-child {
                        border-left: none;
                    }
                    &:last-child {
                        border-right: none;
                    }
                }
            }
        }
        :host ::ng-deep .s25-ng-table--cell.stateTentative {
            font-style: italic;
        }

        :host ::ng-deep .s25-ng-table--cell.stateCancelled {
            color: var(--conflict-color);
            font-weight: 400;
        }

        :host ::ng-deep .s25-ng-table--cell.stateRequested_changes {
            color: var(--color--requested-changes);
            font-weight: 400;
        }

        :host ::ng-deep .s25ngTable .s25-ng-table--table {
            width: max-content;

            > thead {
                border: none;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25ObjectSearchResultsListComponent {
    readonly itemTypeId = input<Item.Id>(Item.Ids.Location);
    readonly isTempSearch = input<boolean>();
    readonly profileId = input<number>();
    readonly eventId = input<number>();
    readonly occ = input<S25Reservation>();
    readonly params = input<UrlParams | undefined>({});
    readonly showLocationScore = input<boolean>();
    readonly hideConflicts = input<boolean>();
    readonly hideRequestConflicts = input<boolean>();
    readonly selectedItems = input<number[] | undefined>([]);
    readonly event = input<S25Event>();
    readonly isEventForm = input<boolean>(false);
    readonly hideInstructions = input<boolean>(false);
    readonly excludeExpress = input<boolean>(false);
    readonly profilePrefSearch = input<DropDownItem<string>>();
    readonly savedSearchChosen = input<DropDownItem<number>>();
    readonly suppressedDatetimeFields = input<string[]>([]);

    readonly refreshF = output<ObjectSearchRefresh>();
    readonly cancelled = output<void>();
    readonly resultsLoaded = output<number>();
    /** favorite search came back with no results */
    readonly starredSearchEmpty = output<void>();

    readonly layoutConflictModal = viewChild<S25ModalComponent>("layoutConflictModal");
    readonly tableComponent = viewChild(S25TableComponent);

    readonly fls = inject(FlsStore);
    S25Const = S25Const;
    data: SearchResponseData = {};
    sortColumn?: string;
    sortOrder?: "asc" | "desc" | null = "asc";
    chosenModels: S25ItemI[] = [];
    scrollHasMorePages: boolean = false;
    pageSize: number = 25;

    readonly displayLocationScore = s25Resource(() => Eval25Service.displayLocationScore());

    readonly namePlural = computed(() => S25ObjectMap[this.itemTypeId()].name.plural);
    readonly nameSingular = computed(() => S25ObjectMap[this.itemTypeId()].name.singular);
    readonly nodeId = computed(() => S25ObjectMap[this.itemTypeId()].nodeId);
    readonly nodeName = computed(() => S25ObjectMap[this.itemTypeId()].nodeName);
    readonly addSelectedButton = computed(
        () => `Add Selected ${this.itemTypeId() === Item.Ids.Resource ? "Resource" : "Location"}`,
    );
    readonly allLocations = computed((): S25RmReservation[] =>
        S25ReservationUtil.getObjectsFlat(this.occ()?.locations?.[0]),
    );
    readonly allResources = computed((): S25RsReservation[] =>
        S25ReservationUtil.getObjectsFlat(this.occ()?.resources?.[0]),
    );
    readonly allowSelection = computed(() => !this.isEventForm());
    readonly canEditLayoutSetup = computed(
        () => !this.isEventForm() || !this.suppressedDatetimeFields().includes("setup"),
    );
    readonly canEditLayoutTakedown = computed(
        () => !this.isEventForm() || !this.suppressedDatetimeFields().includes("takedown"),
    );

    newAddedItemsList: ObjectSearchAvail.AddedItem[] = [];
    waitlistButtonInstances: S25ObjectAddButtonComponent[] = [];
    isMultiSelected: boolean = false;

    tableData: Table.InfiniteScroll = {
        type: "infinite scroll",
        dataSource: this.getRows,
        columns: [],
        hasMorePages: this.hasMorePages,
        customRowCount: this.getRowCount,
    };

    profileSetupMinutes = 0;
    profileTakedownMinutes = 0;

    layoutDetail: LayoutTimesDuration = {};

    private pendingAddContext?: {
        data: ObjectSearchAvail.AddedItem[];
        isAdd: boolean;
        multiSelect: boolean;
        buttonInstance?: S25ObjectAddButtonComponent;
    };

    constructor(private cd: ChangeDetectorRef) {
        AngBridgeUtil.init(this);
        effect(() => {
            const baseColumns = S25ObjectMap[this.itemTypeId()]?.columns ?? [];
            this.tableData.columns = this.hideInstructions()
                ? baseColumns.filter((column) => column.id !== "instructions")
                : baseColumns;
        });
    }

    scrollTableToTop() {
        const elem = this.tableComponent()?.infiniteScrollElement?.nativeElement;
        elem?.scroll({ top: 0, behavior: "smooth" });
    }

    getRequestRowIndex(itemId: number): number | undefined {
        return this.tableComponent()?.getRowIndex(itemId);
    }

    getRequestCellHandle(rowIndex: number, colPrefname: string) {
        const columnId = colPrefname === "request" ? "add" : colPrefname;
        const instance = this.tableComponent()?.getCell(rowIndex, columnId)?.instance as
            | S25ObjectAddButtonComponent
            | undefined;
        if (!instance) return undefined;

        // unless we've already hooked into this instance...
        if (!Object.hasOwn(instance, "hideAction")) {
            // intercept angJS calling cell.hideAction = true/false
            S25Util.onPropertyChange(instance, "hideAction", (newValue) => {
                instance.hasAddedItem.set(!!newValue);
            });
        }
        return instance;
    }

    revertPendingAddButtons(items: ObjectSearchAvail.AddedItem[]) {
        for (const addedItem of items ?? []) {
            const itemId = ObjectSearchAvail.getAddedItemId(addedItem);
            const rowIndex = this.getRequestRowIndex(itemId);
            if (rowIndex == null) continue;
            this.getRequestCellHandle(rowIndex, "request")?.revertState();
        }
    }

    @Bind
    async getRows(query: Table.Query): Promise<Table.DataSourceResponse> {
        const data = (await this.getData(query)) || [];
        const searchQuerydata = S25Util.array.forceArray(data || []) as AddItemType[];
        const pageData = this.data?.[this.namePlural()];
        const totalRows = pageData?.total_results ?? 0;
        this.resultsLoaded.emit(totalRows);
        return {
            rows: searchQuerydata.map((row: AddItemType) => this.mapToRow(row)),
            totalRows,
            cacheId: pageData?.paginate_key,
        };
    }

    async getData(tableQuery: Table.Query) {
        if (tableQuery.forceRefresh) tableQuery = { ...tableQuery, cacheId: undefined };
        if (tableQuery.sortColumn.id !== this.sortColumn || tableQuery.sortColumn.order !== this.sortOrder) {
            tableQuery = { ...tableQuery, cacheId: undefined, page: 0 };
            const table = this.tableComponent();
            if (table) table.currentPage = 0;
        }
        this.sortColumn = tableQuery.sortColumn.id;
        this.sortOrder = tableQuery.sortColumn.order;

        const params = this.params() ?? {};
        const apiPage = tableQuery.page + 1; // s25-table uses 0-based index but WS uses 1-based
        const pageParam = `&page=${apiPage}&page_size=${this.pageSize}`;
        const paginateParam = tableQuery.cacheId ? `&paginate=${tableQuery.cacheId}` : `&paginate`;
        const sortColumnId = tableQuery.sortColumn.id
            ? (ListService.ListSortColMap[tableQuery.sortColumn.id] ??
              objectSearchSortColumnMap[tableQuery.sortColumn.id] ??
              tableQuery.sortColumn.id)
            : undefined;
        const sortParam =
            sortColumnId && tableQuery.sortColumn.order
                ? `&order=${tableQuery.sortColumn.order}&sort=${sortColumnId}`
                : "";
        let urlParmas = `${params.criteria}${params.include}${sortParam}${pageParam}${paginateParam}`;
        if (params.inputParam !== "") urlParmas = urlParmas + `${params.inputParam}`;
        if (parseInt(params.query || "") !== 0) urlParmas = urlParmas + `${params.query}`;
        this.data = await S25ObjectMap[this.itemTypeId()].searchQuery(urlParmas);
        this.notifyIfStarredSearchEmpty(params.query, tableQuery.page);

        const searchQuerydata = S25Util.array.forceArray(
            this.data?.[this.namePlural()]?.[this.nameSingular()] || [],
        ) as AddItemType[];

        searchQuerydata.forEach((item: AddItemType) => {
            item.itemTypeId = this.itemTypeId();
            if (item.space_id != null) {
                item.space_id = S25Util.parseInt(item.space_id);
            }
            if (item.resource_id != null) {
                item.resource_id = S25Util.parseInt(item.resource_id);
            }
            item.itemId = item.space_id ?? item.resource_id;
            //assume sharing, if they can, bc on add we will auto-share for them anyway...
            //got this logic from s25-object-search-list-availability.js
            item.share = this.fls.hasFull("EVENT_SHARE") ? "T" : "F";
        });

        let availItems: (CheckedSpaceReservation | CheckedResourceReservation)[] = [];

        if (searchQuerydata.length > 0) {
            const availData: (CheckedSpaceReservation | CheckedResourceReservation)[] =
                await S25ObjectSearchUtil.getAvailData(
                    S25ObjectMap[this.itemTypeId()].objectType,
                    this.profileId() ?? 0,
                    this.eventId() ?? 0,
                    searchQuerydata as any, // Type conversion needed due to structural differences
                    this.event()!,
                    this.selectedItems() ?? [],
                );

            const itemMap = new Map<number, AddItemType>();

            searchQuerydata.forEach((item: AddItemType) => {
                const nodeIdValue = item[this.nodeId()];
                if (typeof nodeIdValue === "number") {
                    itemMap.set(nodeIdValue, item);
                }
            });

            availItems = availData;
        }

        if (this.itemTypeId() === Item.Ids.Location && availItems.length > 0) {
            const isAssociatedLocationsSearch = S25ObjectSearchUtil.isAssociatedLocationsSearch(
                this.savedSearchChosen(),
                this.profilePrefSearch(),
                params.query,
            );
            S25ObjectSearchUtil.filterLocationSearchResults(
                availItems as { assign_perm?: string; direct_schedule?: unknown }[],
                {
                    excludeExpress: this.excludeExpress(),
                    isAssociatedLocationsSearch,
                },
            );
        }

        if (this.hideConflicts() || this.hideRequestConflicts()) {
            //only do if at least one conflict filter is on
            S25Util.array.inplaceFilter(availItems, (item: CheckedSpaceReservation | CheckedResourceReservation) => {
                //Exit before looking at all conflicts if we can skip processing the array of conflcits
                if (!item.hasConflicts) return true;
                const hideConflicts = this.hideConflicts();
                if (item.hasRealConflicts && hideConflicts) return false;

                const conflicts = S25Util.array.forceArray(item?.checkedObj?.conflicts);
                const hasRequestConflict = conflicts.some((c) => ["req_rsrv", "req_block"].includes(c.conflictType));
                // hasNonRequestConflicts is just !hasRequestConflict if there are conflicts
                const hasNonRequestConflicts = conflicts.length > 0 && !hasRequestConflict;

                if (this.hideRequestConflicts() && hasRequestConflict) return false;

                if (hideConflicts && hasNonRequestConflicts) return false;

                return true;
            });
        }

        const pageData = this.data?.[this.namePlural()];
        this.scrollHasMorePages = (pageData?.page_num ?? 0) < (pageData?.page_count ?? 0);

        const columns = this.tableData.columns;

        if (this.itemTypeId() === Item.Ids.Location) {
            const ratingIndex = columns.findIndex((column: Table.Column) => column.id === "rating_avg");
            const showLocationScore = this.showLocationScore();
            if (showLocationScore && this.displayLocationScore.value() && ratingIndex === -1) {
                columns.push({ id: "rating_avg", header: "Location Satisfaction" });
            } else if ((!showLocationScore || !this.displayLocationScore.value()) && ratingIndex !== -1) {
                columns.splice(ratingIndex, 1);
            }
        }

        this.tableComponent()?.setColumns(columns);
        if (this.isTempSearch()) this.deleteTempSearch();
        this.cd.detectChanges();

        return availItems;
    }

    private notifyIfStarredSearchEmpty(query: string | undefined, page: number) {
        if (page !== 0 || query !== "&favorite=T") return;
        const items = this.data?.[this.namePlural()]?.[this.nameSingular()];
        if (items == null) this.starredSearchEmpty.emit();
    }

    @Bind
    mapToRow(row: AddItemType): Table.Row {
        const cells: Table.Row["cells"] = {};
        const { id, itemName } = this.getIdAndName(row);

        for (let i = 0; i < this.tableData.columns.length; i++) {
            const [key, val] = this.getCell(this.tableData.columns[i], row)!;
            cells[key] = val;
        }

        return {
            id: id,
            name: itemName,
            cells,
        };
    }

    getCell(column: Table.Column, row: AddItemType): [string, Table.Cell] | undefined {
        let capacity = row?.max_capacity ?? 0;
        let defaultLayout: Layout;
        if (column.id === "max_capacity" || column.id === "layout") {
            capacity = row?.max_capacity;
            if (row?.layout) {
                defaultLayout = S25Util.propertyGetParentWithChildValue(row?.layout, "default_layout", "T");
                if (defaultLayout?.layout_capacity) capacity = defaultLayout?.layout_capacity;
                if (column.id === "layout") {
                    row.layout.forEach((layout: any) => {
                        layout.itemId = layout.layout_id; // Assign itemId from layout_id
                        layout.itemName = layout.layout_name; // Assign itemName from layout_name
                    });
                }
            }
        }

        let find = undefined;
        const itemTypeId = this.itemTypeId();
        if (itemTypeId === Item.Ids.Location) {
            find = this.allLocations().find((item) => item.itemId === row.space_id);
        } else {
            find = this.allResources().find((item) => item.itemId === row.resource_id);
        }

        switch (column.id) {
            case "add":
                return [
                    "add",
                    {
                        component: S25ObjectAddButtonComponent,
                        inputs: {
                            data: row,
                            occ: this.occ,
                            itemTypeId: itemTypeId,
                            allowSelection: this.allowSelection,
                            isEventForm: this.isEventForm,
                        },
                        outputs: {
                            onButtonClick: (data, context: Table.CellCallbackContext<S25ObjectAddButtonComponent>) => {
                                if (data.closeModal) {
                                    this.emitPendingAdds();
                                    return;
                                }
                                if (data.waitlistItem && data.multiSelected) {
                                    if (data.isAdd) {
                                        this.waitlistButtonInstances.push(context.instance);
                                    } else {
                                        this.waitlistButtonInstances = this.waitlistButtonInstances.filter(
                                            (i) => i !== context.instance,
                                        );
                                    }
                                    this.isMultiSelected =
                                        this.newAddedItemsList.length > 0 || this.waitlistButtonInstances.length > 0;
                                    return;
                                }
                                this.addItem(
                                    data.data ?? [],
                                    data.isAdd ?? false,
                                    data.multiSelected ?? false,
                                    context.instance,
                                );
                            },
                        },
                    } satisfies Table.Cell<S25ObjectAddButtonComponent>,
                ];

            case "space_name":
                return [
                    "space_name",
                    {
                        component: S25ItemComponent,
                        inputs: {
                            itemTypeId: Item.Ids.Location,
                            itemId: row.space_id,
                            itemName: row.space_name,
                        },
                    },
                ];

            case "conflicts":
                return [
                    "conflicts",
                    {
                        component: S25ObjectConflictComponent,
                        inputs: { hasConflict: S25Util.toBool(row?.hasConflicts), data: row },
                    },
                ];

            case "formal_name":
                return ["formal_name", { text: row.formal_name }];

            case "max_capacity":
                return ["max_capacity", { text: capacity }];

            case "available_dates":
                return [
                    "available_dates",
                    {
                        text: `${row.checkedObj.availableDates < 0 ? 0 : row.checkedObj.availableDates} / ${row.checkedObj.totalDatesChecked}`,
                    },
                ];

            case "rating_avg":
                return [
                    "rating_avg",
                    { text: row?.room_rating?.rating_avg ? row?.room_rating?.rating_avg + " / 5.0" : "-" },
                ];

            case "building_id":
                return ["building_id", { text: row?.building_name ? row.building_name : "-" }];

            case "resource_name":
                return [
                    "resource_name",
                    {
                        component: S25ItemComponent,
                        inputs: {
                            itemTypeId: Item.Ids.Resource,
                            itemId: row.resource_id,
                            itemName: row.resource_name,
                        },
                    },
                ];

            case "stock_level":
                return [
                    "stock_level",
                    {
                        text: row.availability,
                    },
                ];

            case "instructions":
                return [
                    "instructions",
                    {
                        component: S25EditableTextareaComponent,
                        inputs: { val: find?.instructions || row.instructions, readOnly: !!find, renderHtml: true },
                        outputs: {
                            valChange: (val: string, context: { row: Table.Row<any, any>; cell: any }) => {
                                const add = context.row?.cells?.add;
                                if (!add?.inputs?.data) {
                                    throw new Error("Unexpected table row format: add.inputs.data missing");
                                }

                                add.inputs.data.instructions = val; // single item add
                                // for multiple items
                                const itemIdToMatch = this.getIdAndName(add.inputs.data).id;
                                const existingItem = this.newAddedItemsList.find(
                                    (item) => ObjectSearchAvail.getAddedItemId(item) === itemIdToMatch,
                                );
                                if (!existingItem) {
                                    console.warn("No matching item found:", add.inputs.data);
                                    return;
                                }
                                existingItem.instructions = val;
                            },
                        },
                    } satisfies Table.Cell<S25EditableTextareaComponent>,
                ];

            // blocked by ACC:-2268,  commented out for now
            /*
            case "layout":
                if (row?.layout && !find) {
                    return [
                        "layout",
                        {
                            component: S25GenericDropdownComponent,
                            inputs: { items: row?.layout, chosen: defaultLayout?.layout_id || 0 },
                            outputs: {
                                chosenChange: (chosenChange: any, row: Table.Row) => {
                                    this.resetDefaultLayout(
                                        row.cells.add.inputs.data.layout,
                                        chosenChange.layout_id,
                                        defaultLayout?.layout_id || 0,
                                    );
                                },
                            },
                        },
                    ];
                } else {
                    return [
                        "layout",
                        {
                            text: defaultLayout?.layout_name || "",
                        },
                    ];
                } */
        }
    }

    @Bind
    onRefresh() {
        return this.tableComponent()?.refresh(true);
    }

    @Bind
    getIdAndName(item: AddItemType): { id: number; itemName: string } {
        if ("space_id" in item) {
            return {
                id: item.space_id,
                itemName: item.space_name,
            };
        } else if ("resource_id" in item) {
            return {
                id: item.resource_id,
                itemName: item.resource_name,
            };
        }
        return { id: 0, itemName: "" };
    }

    @Bind
    getRowCount(counts: Table.RowCounts) {
        const type =
            S25Util.firstCharToUpper(S25Const.itemId2Name[this.itemTypeId()]) + (counts.total === 1 ? "" : "s");
        return `${counts.total} Matching ${type}`;
    }

    @Bind
    hasMorePages() {
        return this.scrollHasMorePages;
    }

    deleteTempSearch() {
        const query = this.params()?.query ?? "";
        if (query !== "") {
            // Handle subscriber_key for contact searches
            if (query.indexOf("subscriber_key") > -1) {
                const regex = /subscriber_key=(\d+)/;
                const match = query.match(regex);
                if (match) {
                    const subscriberKey: number = parseInt(match[1]);
                    return SearchService.deleteTempSubscriberSetByKey(subscriberKey);
                }
            }
            // Handle query_id for other object types
            else if (query.indexOf("query_id") > -1) {
                const regex = /query_id=(\d+)/;
                const match = query.match(regex);
                if (match) {
                    const queryId: number = parseInt(match[1]);
                    if (queryId) return SearchService.deleteTempSearchByQueryId(queryId, this.itemTypeId());
                }
            }
        }
    }

    async addItem(
        data: ObjectSearchAvail.AddedItem[],
        isAdd: boolean,
        multiSelect: boolean,
        buttonInstance?: S25ObjectAddButtonComponent,
        isRetry: boolean = false,
    ) {
        this.isMultiSelected = multiSelect || this.waitlistButtonInstances.length > 0;
        const addNewItem = data[0];
        if (!addNewItem) return;

        const isValidMode = (isAdd && multiSelect) || (!isAdd && !multiSelect);

        let isProfileUpdate = false;
        let setupDiff = 0;
        let takedownDiff = 0;
        let setupISO = "";
        let takedownISO = "";
        const hasPartialRealAvailability = this.hasPartialRealAvailability(addNewItem);

        // ---- DEFAULT LAYOUT TIME LOGIC ----
        if ("layouts" in addNewItem && addNewItem.layouts && isValidMode) {
            const { setup, takedown } = S25ObjectSearchUtil.maxLayoutTimes(data);
            const profileTimes = S25ObjectSearchUtil.getProfileTimes(this.event()!, this.profileId()!);

            this.profileSetupMinutes = profileTimes.setup;
            this.profileTakedownMinutes = profileTimes.takedown;

            setupDiff = setup - profileTimes.setup;
            takedownDiff = takedown - profileTimes.takedown;

            if (setupDiff > 0 || takedownDiff > 0) {
                // Reserve Available: skip segment-wide layout extension check; still apply profile setup/takedown.
                // Post-Confirm retry: user already saw the modal — proceed with add even if conflict remains.
                if (!hasPartialRealAvailability && !isRetry) {
                    const profile = this.event()?.profile?.find((p) => p.itemId === this.profileId());

                    // ANG-7706: setup/takedown is profile-wide — check every reservation on the segment,
                    // not only the occurrence(s) currently selected for the add.
                    const reservations =
                        profile?.reservations?.filter(
                            (r): r is S25Reservation => "itemId" in r && r.itemId !== undefined,
                        ) ?? [];

                    const dates = S25ReservationUtil.getReservationsStartEndDates(reservations);
                    const adjustedDates = S25ObjectSearchUtil.adjustDates(dates, setupDiff, takedownDiff);

                    const rsrvObjects = (await S25ReservationUtil.getRsrvsObjects(reservations)) ?? {
                        allLocations: [],
                        allResources: [],
                    };
                    const existingSpaceIds = S25Util.toItemIds(rsrvObjects.allLocations as S25RmReservation[]);
                    const allSpaceIds: number[] = [...new Set([addNewItem.spaceId, ...existingSpaceIds])];

                    const resources = (rsrvObjects.allResources as S25RsReservation[]).filter(
                        (resource) => !!resource.itemId,
                    ) as S25ItemI[];
                    const [spaceAvailability, resourceAvailability] = await Promise.all([
                        SpaceService.getSpaceDatesAvailability(
                            allSpaceIds,
                            adjustedDates,
                            this.eventId(),
                            this.profileId(),
                        ),
                        resources.length
                            ? ResourceService.getResourceDatesAvailability(
                                  resources,
                                  adjustedDates,
                                  this.eventId()!,
                                  this.profileId()!,
                              )
                            : Promise.resolve(undefined),
                    ]);

                    const hasNonOverridableSpaceConflict = spaceAvailability?.spaces?.space?.some(
                        (space: WSSpaceAvail) =>
                            space.has_conflicts === "T" &&
                            !space.dates?.every(
                                (date: DateElement) =>
                                    !date.conflict?.length ||
                                    date.conflict.every((conflict) =>
                                        S25ObjectSearchUtil.canOverrideConflict(
                                            conflict.conflict_type,
                                            this.fls.value(),
                                        ),
                                    ),
                            ),
                    );
                    const hasResourceConflict = resourceAvailability?.resources?.resource?.some(
                        (resource: WSResourceAvail) => resource.has_conflicts === "T",
                    );

                    if (hasNonOverridableSpaceConflict || hasResourceConflict) {
                        this.layoutDetail = {
                            itemName: addNewItem.spaceName,
                            setup: S25Util.minutesToISODuration(setup),
                            takedown: S25Util.minutesToISODuration(takedown),
                            conflicts: S25ObjectSearchUtil.buildLayoutExtensionConflicts(
                                spaceAvailability,
                                resourceAvailability,
                                this.fls.value(),
                            ),
                        };

                        // Store context for retry vs edited Layout times
                        this.pendingAddContext = {
                            data,
                            isAdd,
                            multiSelect,
                            buttonInstance,
                        };

                        const layoutConflictModal = this.layoutConflictModal();
                        if (!layoutConflictModal?.open()) layoutConflictModal?.open();

                        // Revert the button state to original, adding default layout extra setup/takedown time not longer available
                        return buttonInstance?.revertState();
                    }
                }

                // No conflict - if this is a retry in multiSelect mode, re-select the item
                if (isRetry && multiSelect && buttonInstance) {
                    buttonInstance.selectItem();
                }

                setupISO = S25Util.minutesToISODuration(setupDiff > 0 ? setup : profileTimes.setup);
                takedownISO = S25Util.minutesToISODuration(takedownDiff > 0 ? takedown : profileTimes.takedown);
                isProfileUpdate = true;
            }
        }

        // ---- UPDATE SELECTED ITEMS ----
        if (multiSelect) {
            if (isAdd) {
                if ("resourceId" in addNewItem && addNewItem.resourceId) {
                    const existing = this.newAddedItemsList.find(
                        (item): item is ObjectSearchAvail.AddedResource =>
                            "resourceId" in item && item.resourceId === addNewItem.resourceId,
                    );
                    existing ? (existing.quantity = addNewItem.quantity) : this.newAddedItemsList.push(addNewItem);
                } else {
                    this.newAddedItemsList.push(addNewItem);
                }
            } else {
                this.newAddedItemsList = this.newAddedItemsList.filter((i) => i !== addNewItem);
            }
            return;
        }

        // ---- SINGLE SELECT (DEBOUNCED SAVE) ----
        if (!this.newAddedItemsList.includes(addNewItem)) {
            this.newAddedItemsList.push(addNewItem);
        }

        this.doAdd(isProfileUpdate, setupISO, takedownISO);
    }

    @Debounce(1000)
    private doAdd(isProfileUpdate: boolean, setupISO: string, takedownISO: string) {
        const output: ObjectSearchRefresh = { items: [...this.newAddedItemsList] };
        if (isProfileUpdate) output.profileRange = { setup: setupISO, takedown: takedownISO };
        this.emitPendingAdds(output);
    }

    async saveMulitSelectedItems() {
        if (this.waitlistButtonInstances.length > 0) {
            const waitlistPromises = this.waitlistButtonInstances.map((instance) => instance.addToWaitlist());
            await Promise.all(waitlistPromises);
            this.waitlistButtonInstances = [];
        }
        const pendingAdds = [...this.newAddedItemsList];
        const output: ObjectSearchRefresh = { items: pendingAdds };

        const { setup, takedown } = S25ObjectSearchUtil.maxLayoutTimes(pendingAdds);
        const setupDiff = setup - this.profileSetupMinutes;
        const takedownDiff = takedown - this.profileTakedownMinutes;

        if (setupDiff > 0 || takedownDiff > 0) {
            output.profileRange = {
                setup: S25Util.minutesToISODuration(setupDiff > 0 ? setup : this.profileSetupMinutes),
                takedown: S25Util.minutesToISODuration(takedownDiff > 0 ? takedown : this.profileTakedownMinutes),
            };
        }

        this.emitPendingAdds(output);
    }

    private emitPendingAdds(output?: ObjectSearchRefresh): void {
        const payload = output ?? { items: [...this.newAddedItemsList] };
        if (!payload.items?.length) return;
        this.refreshF.emit(payload);
        this.newAddedItemsList = [];
    }

    onCancel() {
        this.cancelled.emit();
    }

    resetDefaultLayout(layouts: Layout[], newDefaultId: number, oldDefaultId: number) {
        return layouts.forEach((layout: Layout) => {
            if (layout.layout_id === oldDefaultId) layout.default_layout = "F";
            if (layout.layout_id === newDefaultId) layout.default_layout = "T";
        });
    }

    layoutTimesChange($event: { takedown: string; setup: string; confirmed: boolean }) {
        this.layoutConflictModal()?.close();

        if (!$event.confirmed || !this.pendingAddContext) {
            this.pendingAddContext = undefined;
            return;
        }

        const { data, isAdd, multiSelect, buttonInstance } = this.pendingAddContext;
        this.pendingAddContext = undefined;

        // Update the item's layout times with user-adjusted values
        const addNewItem = data[0];
        if ("layouts" in addNewItem && addNewItem.layouts) {
            // Override layout times with user's adjusted values
            // Update layouts to use the new times
            for (const layout of addNewItem.layouts) {
                layout.layout_setup_tm = $event.setup;
                layout.layout_tdown_tm = $event.takedown;
            }
        }
        // Re-run addItem with modified data
        this.addItem(data, isAdd, multiSelect, buttonInstance, true);
    }

    private hasPartialRealAvailability(item: ObjectSearchAvail.AddedItem): boolean {
        const checkedObj = item.checkedObj;
        if (!checkedObj?.totalDatesChecked) return false;
        const availableDates = checkedObj.availableDatesWithOverrides;
        if (!S25Util.isDefined(availableDates)) return false;
        return availableDates > 0 && availableDates < checkedObj.totalDatesChecked;
    }
}
